import React, { useMemo } from 'react';
import { Container, Title, Grid, Stack, Group, Button, Paper, Text, rem } from '@mantine/core';
import { IconChartPie, IconFileText, IconPlus, IconSend, IconUpload } from '@tabler/icons-react';
import { useJobs } from '../api/jobs';
import StatCard from '../components/dashboard/StatCard';
import QuickStartBanner from '../components/dashboard/QuickStartBanner';
import RecentActivityTable from '../components/dashboard/RecentActivityTable';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/common/PageHeader';

const DashboardPage: React.FC = () => {
    const { data: jobs } = useJobs();
    const navigate = useNavigate();

    // Mock stats for now
    const stats = useMemo(() => {
        const totalApplications = jobs?.length || 0;
        const resumesGenerated = 0; // Placeholder until we fetch artifacts
        const responseRate = "0%";

        // Sort jobs by date desc for recent activity
        const recentJobs = [...(jobs || [])]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 5);

        return { totalApplications, resumesGenerated, responseRate, recentJobs };
    }, [jobs]);

    return (
        <Container size="xl" py="xl">
            <Stack gap="xl">
                {/* Header Section */}
                <PageHeader
                    title="Dashboard"
                    subtitle="Welcome back, here is your career overview."
                    actions={
                        <Group>
                            <Button
                                variant="default"
                                leftSection={<IconUpload size={16} />}
                                onClick={() => navigate('/resumes')}
                            >
                                Upload Resume
                            </Button>
                            <Button
                                leftSection={<IconPlus size={16} />}
                                onClick={() => navigate('/jobs?action=add')}
                            >
                                Add Job From URL
                            </Button>
                        </Group>
                    }
                />

                {/* Key Stats Grid */}
                <Grid>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                        <StatCard
                            label="TOTAL APPLICATIONS"
                            value={stats.totalApplications.toString()}
                            trend="+2 this week"
                            icon={<IconSend size="1.2rem" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                        <StatCard
                            label="RESUMES GENERATED"
                            value={stats.resumesGenerated.toString()}
                            trend="+5 this week"
                            icon={<IconFileText size="1.2rem" />}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                        <StatCard
                            label="RESPONSE RATE"
                            value={stats.responseRate}
                            trend="+5% vs last month"
                            icon={<IconChartPie size="1.2rem" />}
                        />
                    </Grid.Col>
                </Grid>

                {/* Quick Action Panel */}
                <QuickStartBanner />

                {/* Active Applications Section */}
                <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" mb="md">
                        <Title order={3} size="h4">Recent Activity</Title>
                        <Button variant="subtle" size="xs" onClick={() => navigate('/jobs')}>
                            View All
                        </Button>
                    </Group>

                    <RecentActivityTable jobs={stats.recentJobs} />
                </Paper>

            </Stack>
        </Container>
    );
};

export default DashboardPage;
